import type { Mode, Plan } from '@trippath/shared';

import { kmApart } from './map';

/** A stop of the route drawn: where, when it is reached, and its nights. */
export interface RouteStop {
  city: string;
  lat: number;
  lon: number;
  start: string;
  nights: number;
}

/** A way between two stops drawn, by their places in the route. */
export interface RouteLeg {
  mode: Mode | null;
  from: number;
  to: number;
  km: number;
}

/** A trip's route as a poster or a video draws it. */
export interface Route {
  name: string;
  /** The trip's last day. */
  end: string;
  stops: RouteStop[];
  legs: RouteLeg[];
}

/** How the route is drawn: on a dark ground or a light one, its stops named, flights as arcs. */
export interface Look {
  dark: boolean;
  names: boolean;
  arcs: boolean;
}

type At = [x: number, y: number];

/** Each way's colour, as the map draws it; one not chosen grey. */
export const WAY_COLOURS: Record<Mode | 'none', string> = {
  flight: '#3b6ef5',
  train: '#16a34a',
  bus: '#e07b00',
  ferry: '#0e9bb5',
  car: '#db2777',
  foot: '#8a94a6',
  none: '#8a94a6',
};

/** The route of a plan: its stops on the map, and each way on from one to the next. */
export function routeOf(plan: Plan): Route {
  const placed = plan.stops.flatMap((stop) =>
    stop.place
      ? [
          {
            stop,
            at: {
              city: stop.city,
              lat: stop.place.lat,
              lon: stop.place.lon,
              start: stop.start,
              nights: stop.transfer ? 0 : stop.days,
            },
          },
        ]
      : [],
  );
  return {
    name: plan.trip.name,
    end: plan.trip.end,
    stops: placed.map(({ at }) => at),
    legs: placed.slice(1).map(({ at }, index) => {
      const from = placed[index] as (typeof placed)[number];
      return {
        mode: from.stop.out?.mode ?? null,
        from: index,
        to: index + 1,
        km: kmApart(from.at, at),
      };
    }),
  };
}

/** Places the route's stops in a box `width` by `height`, `pad` in from each edge, north up. */
export function projector(stops: RouteStop[], width: number, height: number, pad: number) {
  const lons = stops.map((stop) => stop.lon);
  const lats = stops.map((stop) => stop.lat);
  const [west, east] = [Math.min(...lons, 180), Math.max(...lons, -180)];
  const [south, north] = [Math.min(...lats, 90), Math.max(...lats, -90)];
  const squeeze = Math.cos((((south + north) / 2) * Math.PI) / 180);
  const across = Math.max((east - west) * squeeze, 0.01);
  const down = Math.max(north - south, 0.01);
  const scale = Math.min((width - 2 * pad) / across, (height - 2 * pad) / down);
  // The room left over, by the route's own extent: a single stop sits in the middle.
  const spareX = (width - 2 * pad - (east - west) * squeeze * scale) / 2;
  const spareY = (height - 2 * pad - (north - south) * scale) / 2;
  return (stop: { lat: number; lon: number }): At => [
    pad + spareX + (stop.lon - west) * squeeze * scale,
    pad + spareY + (north - stop.lat) * scale,
  ];
}

/** A flight as an arc: bowed to its left by a fifth of its length. */
export function arcOf([ax, ay]: At, [bx, by]: At, steps = 16): At[] {
  const [mx, my] = [(ax + bx) / 2 - (by - ay) / 5, (ay + by) / 2 + (bx - ax) / 5];
  return Array.from({ length: steps + 1 }, (_, step) => {
    const t = step / steps;
    const u = 1 - t;
    return [u * u * ax + 2 * u * t * mx + t * t * bx, u * u * ay + 2 * u * t * my + t * t * by];
  });
}

/** A way's line through the box: straight, a flight an arc; none for a flight left out. */
export function legLine(
  route: Route,
  leg: RouteLeg,
  at: (stop: RouteStop) => At,
  arcs: boolean,
): At[] {
  const [from, to] = [
    at(route.stops[leg.from] as RouteStop),
    at(route.stops[leg.to] as RouteStop),
  ];
  if (leg.mode !== 'flight') return [from, to];
  return arcs ? arcOf(from, to) : [];
}

/** Where a video is at `t` of its length: the ways drawn whole, the share of the next, its day
 * and the kilometres so far. */
export function momentOf(
  route: Route,
  t: number,
): { legs: number; share: number; day: string; km: number } {
  const position = Math.min(Math.max(t, 0), 1) * route.legs.length;
  const legs = Math.min(Math.floor(position), route.legs.length);
  const share = position - legs;
  const done = route.legs.slice(0, legs).reduce((sum, leg) => sum + leg.km, 0);
  const next = route.legs[legs];
  const stop = route.stops[Math.min(legs + (share > 0.5 ? 1 : 0), route.stops.length - 1)];
  return { legs, share, day: stop?.start ?? '', km: done + (next ? next.km * share : 0) };
}

/** The part of a line from its start to `share` of its points. */
function partOf(line: At[], share: number): At[] {
  const end = Math.max(1, Math.round(share * (line.length - 1)));
  return line.slice(0, end + 1);
}

/** What a frame is drawn on: a canvas's 2D context, or what stands for one. */
export type Painter = Pick<
  CanvasRenderingContext2D,
  | 'fillRect'
  | 'beginPath'
  | 'moveTo'
  | 'lineTo'
  | 'stroke'
  | 'arc'
  | 'fill'
  | 'fillText'
  | 'setLineDash'
> & { fillStyle: unknown; strokeStyle: unknown; lineWidth: number; font: string };

/** One frame of the route's video at `t`: the ground, the ways gone so far, the stops reached
 * and their names, and the day and kilometres in the corner. */
export function drawFrame(
  paint: Painter,
  route: Route,
  look: Look,
  t: number,
  [width, height]: [number, number],
  corner: (day: string, km: number) => string,
): void {
  const at = projector(route.stops, width, height, height / 10);
  const moment = momentOf(route, t);
  paint.fillStyle = look.dark ? '#0f172a' : '#f1f5f9';
  paint.fillRect(0, 0, width, height);
  route.legs.forEach((leg, index) => {
    if (index > moment.legs) return;
    const line = legLine(route, leg, at, look.arcs);
    const drawn = index === moment.legs ? partOf(line, moment.share) : line;
    paint.strokeStyle = WAY_COLOURS[leg.mode ?? 'none'];
    paint.lineWidth = height / 160;
    paint.setLineDash(leg.mode === 'flight' ? [height / 60, height / 90] : []);
    paint.beginPath();
    drawn.forEach(([x, y], step) => {
      if (step === 0) paint.moveTo(x, y);
      else paint.lineTo(x, y);
    });
    paint.stroke();
  });
  const ink = look.dark ? '#f8fafc' : '#111827';
  route.stops.slice(0, moment.legs + 1).forEach((stop) => {
    const [x, y] = at(stop);
    paint.fillStyle = ink;
    paint.beginPath();
    paint.arc(x, y, height / 140, 0, Math.PI * 2);
    paint.fill();
    if (look.names && stop.nights > 0) {
      paint.font = `600 ${String(Math.round(height / 45))}px "IBM Plex Sans", sans-serif`;
      paint.fillText(stop.city, x + height / 80, y + height / 120);
    }
  });
  paint.fillStyle = 'rgba(17, 24, 39, 0.78)';
  paint.fillRect(height / 40, height / 40, width / 4, height / 14);
  paint.fillStyle = '#ffffff';
  paint.font = `${String(Math.round(height / 40))}px "IBM Plex Mono", monospace`;
  paint.fillText(
    corner(moment.day, moment.km),
    height / 40 + height / 60,
    height / 40 + height / 22,
  );
}

/** Records `ms` of a canvas as `draw` paints each frame by its share of the time → the video,
 * MP4 where the browser makes it, else WebM. */
export function recordVideo(
  canvas: HTMLCanvasElement,
  draw: (t: number) => void,
  ms: number,
): Promise<Blob> {
  const type = MediaRecorder.isTypeSupported('video/mp4') ? 'video/mp4' : 'video/webm';
  const recorder = new MediaRecorder(canvas.captureStream(30), { mimeType: type });
  const chunks: Blob[] = [];
  recorder.ondataavailable = (event) => {
    chunks.push(event.data);
  };
  return new Promise((resolve) => {
    recorder.onstop = () => {
      resolve(new Blob(chunks, { type }));
    };
    const began = performance.now();
    const frame = () => {
      const t = (performance.now() - began) / ms;
      draw(Math.min(t, 1));
      if (t < 1) requestAnimationFrame(frame);
      else recorder.stop();
    };
    recorder.start();
    frame();
  });
}
